import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, MapPin, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { label: "Home", full_name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", is_default: false };

export default function Addresses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/addresses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/addresses/${editing}`, form);
      else await api.post("/addresses", form);
      toast.success(editing ? "Address updated" : "Address added");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await api.delete(`/addresses/${id}`);
    load();
  };

  return (
    <div data-testid="addresses-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-2">Addresses</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Shipping addresses.</h1>
        </div>
        <Button data-testid="add-address" onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md"><Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> Add address</Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-md p-12 text-center text-slate-500">
          <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-3" strokeWidth={1.5}/>
          No addresses saved yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((a) => (
            <div key={a.address_id} className="border border-slate-200 rounded-md p-5 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-display text-lg font-semibold text-slate-900">{a.label}</div>
                    {a.is_default && <Star className="w-3.5 h-3.5 text-blue-600 fill-blue-600" strokeWidth={1.5}/>}
                  </div>
                  <div className="text-sm text-slate-700 mt-2">{a.full_name}</div>
                  <div className="text-sm text-slate-600 mt-1">{a.line1}{a.line2 && `, ${a.line2}`}<br/>{a.city}, {a.state} {a.postal_code}<br/>{a.country}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setForm({ ...EMPTY, ...a }); setEditing(a.address_id); setOpen(true); }} className="p-1.5 rounded-md hover:bg-slate-100"><Pencil className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.75}/></button>
                  <button onClick={() => del(a.address_id)} className="p-1.5 rounded-md hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-500" strokeWidth={1.75}/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit address" : "Add address"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input required placeholder="Label (Home / Lab)" value={form.label} onChange={(e) => setForm({...form, label: e.target.value})}/>
            <Input required placeholder="Full name" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})}/>
            <Input required placeholder="Street address" value={form.line1} onChange={(e) => setForm({...form, line1: e.target.value})}/>
            <Input placeholder="Apt, suite (optional)" value={form.line2} onChange={(e) => setForm({...form, line2: e.target.value})}/>
            <div className="grid grid-cols-3 gap-2">
              <Input required placeholder="City" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}/>
              <Input required placeholder="State" value={form.state} onChange={(e) => setForm({...form, state: e.target.value})}/>
              <Input required placeholder="ZIP" value={form.postal_code} onChange={(e) => setForm({...form, postal_code: e.target.value})}/>
            </div>
            <Input required placeholder="Country" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}/>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({...form, is_default: e.target.checked})}/>
              Set as default shipping address
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">{editing ? "Save" : "Add"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
